/**
 * <cart-validation-element>
 *
 * Drives the Peru address (Departamento/Provincia/Distrito) + comprobante
 * (Boleta/Factura) drawer rendered by snippets/cart-checkout-peru.liquid.
 *
 * - Opens instead of the default cart-form submit when a [data-peru-checkout-open]
 *   trigger exists on the page (gated by the "Enable Peru checkout" theme setting).
 * - Cascades Departamento -> Provincia -> Distrito selects from a static
 *   ubigeo dataset (data-ubigeo-url).
 * - Pushes the selected fields to the cart as attributes on every change
 *   (POST data-cart-update-url, Shopify's /cart/update.js).
 * - On "Continuar al pago", validates the fields and redirects to
 *   data-checkout-url with shipping-address + comprobante query params.
 */
(function () {
  'use strict';

  class CartValidationElement extends HTMLElement {
    constructor() {
      super();

      this.dialog = this.querySelector('[data-cart-checkout-peru-dialog]');
      this.departamentoSelect = this.querySelector('[data-peru-departamento]');
      this.provinciaSelect = this.querySelector('[data-peru-provincia]');
      this.distritoSelect = this.querySelector('[data-peru-distrito]');
      this.comprobanteRadios = this.querySelectorAll('[data-peru-comprobante]');
      this.boletaFields = this.querySelector('[data-peru-boleta-fields]');
      this.facturaFields = this.querySelector('[data-peru-factura-fields]');
      this.tipoDocumentoSelect = this.querySelector('[data-peru-tipo-documento]');
      this.numeroDocumentoInput = this.querySelector('[data-peru-numero-documento]');
      this.numeroRucInput = this.querySelector('[data-peru-numero-ruc]');
      this.continueButton = this.querySelector('[data-peru-continue]');
      this.errorMessage = this.querySelector('[data-peru-error]');
      this.closeButtons = this.querySelectorAll('[data-peru-checkout-close]');

      this.ubigeoUrl = this.dataset.ubigeoUrl;
      this.cartUpdateUrl = this.dataset.cartUpdateUrl;
      this.checkoutUrl = this.dataset.checkoutUrl;
      this.selectPlaceholder = this.dataset.selectPlaceholder || '';
      this.boletaLabel = this.dataset.boletaLabel || 'Boleta';
      this.facturaLabel = this.dataset.facturaLabel || 'Factura';
      this.requiredError = this.dataset.requiredError || 'This field is required';
      this.invalidDniError = this.dataset.invalidDniError || 'Invalid document number';
      this.invalidRucError = this.dataset.invalidRucError || 'Invalid RUC number';

      this.ubigeoData = null;
      this.isAnimating = false;
      this.a11y = window.theme && window.theme.a11y;

      this.queueCartAttributesUpdate = this.debounce(() => this.updateCartAttributes(), 400);
    }

    connectedCallback() {
      this.trigger = document.querySelector('[data-peru-checkout-open]');
      this.trigger?.addEventListener('click', this.onTriggerClick.bind(this));

      this.closeButtons.forEach((button) => button.addEventListener('click', () => this.close()));

      this.dialog.addEventListener('click', (event) => {
        if (event.target.nodeName === 'DIALOG') this.close();
      });

      this.dialog.addEventListener('keydown', (event) => {
        if (event.code === 'Escape') {
          event.preventDefault();
          this.close();
        }
      });

      this.departamentoSelect.addEventListener('change', this.onDepartamentoChange.bind(this));
      this.provinciaSelect.addEventListener('change', this.onProvinciaChange.bind(this));
      this.distritoSelect.addEventListener('change', () => this.queueCartAttributesUpdate());

      this.comprobanteRadios.forEach((radio) => radio.addEventListener('change', this.onComprobanteChange.bind(this)));

      this.tipoDocumentoSelect.addEventListener('change', () => this.queueCartAttributesUpdate());
      this.numeroDocumentoInput.addEventListener('input', () => this.queueCartAttributesUpdate());
      this.numeroRucInput.addEventListener('input', () => this.queueCartAttributesUpdate());

      this.continueButton.addEventListener('click', this.onContinueClick.bind(this));

      this.fetchUbigeoData();
    }

    debounce(fn, wait) {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn(...args), wait);
      };
    }

    onTriggerClick(event) {
      event.preventDefault();
      this.open();
    }

    fetchUbigeoData() {
      if (!this.ubigeoUrl || this.ubigeoData) return Promise.resolve(this.ubigeoData);

      return fetch(this.ubigeoUrl)
        .then((response) => response.json())
        .then((data) => {
          this.ubigeoData = data;
          this.renderDepartamentoOptions();
          return data;
        })
        .catch((error) => console.error(error));
    }

    getDepartamento(name) {
      return this.ubigeoData ? this.ubigeoData.find((item) => item.name === name) : null;
    }

    getComprobanteTipo() {
      const checked = Array.from(this.comprobanteRadios).find((radio) => radio.checked);
      return checked ? checked.value : 'boleta';
    }

    fillSelect(select, values, getValue, getLabel) {
      select.innerHTML = '';

      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = this.selectPlaceholder;
      select.appendChild(placeholder);

      values.forEach((item) => {
        const option = document.createElement('option');
        option.value = getValue(item);
        option.textContent = getLabel(item);
        select.appendChild(option);
      });
    }

    renderDepartamentoOptions() {
      if (!this.ubigeoData) return;

      this.fillSelect(
        this.departamentoSelect,
        this.ubigeoData,
        (departamento) => departamento.name,
        (departamento) => departamento.name
      );
    }

    onDepartamentoChange() {
      const departamento = this.getDepartamento(this.departamentoSelect.value);

      this.fillSelect(this.provinciaSelect, departamento ? departamento.provincias : [], (provincia) => provincia.name, (provincia) => provincia.name);
      this.provinciaSelect.disabled = !departamento;

      this.fillSelect(this.distritoSelect, [], () => '', () => '');
      this.distritoSelect.disabled = true;

      this.hideError();
      this.queueCartAttributesUpdate();  
    }

    onProvinciaChange() {
      const departamento = this.getDepartamento(this.departamentoSelect.value);
      const provincia = departamento ? departamento.provincias.find((item) => item.name === this.provinciaSelect.value) : null;

      this.fillSelect(this.distritoSelect, provincia ? provincia.distritos : [], (distrito) => distrito, (distrito) => distrito);
      this.distritoSelect.disabled = !provincia;

      this.hideError();
      this.queueCartAttributesUpdate();
    }

    onComprobanteChange() {
      const isFactura = this.getComprobanteTipo() === 'factura';

      this.boletaFields.classList.toggle('is-hidden', isFactura);
      this.facturaFields.classList.toggle('is-hidden', !isFactura);

      if (isFactura) {
        this.numeroDocumentoInput.value = '';
      } else {
        this.numeroRucInput.value = '';
      }

      this.hideError();
      this.queueCartAttributesUpdate();
    }

    updateCartAttributes() {
      if (!this.cartUpdateUrl) return Promise.resolve();

      const isFactura = this.getComprobanteTipo() === 'factura';
      const tipoDocumentoOption = this.tipoDocumentoSelect.selectedOptions[0];
      
      console.log(this.buildCheckoutUrl());
      
      const attributes = {
        Departamento: this.departamentoSelect.value,
        Provincia: this.provinciaSelect.value,
        Distrito: this.distritoSelect.value,
        'Tipo de comprobante': isFactura ? this.facturaLabel : this.boletaLabel,
        'Tipo de documento': isFactura ? '' : tipoDocumentoOption ? tipoDocumentoOption.textContent : '',
        'Número de documento': isFactura ? '' : this.numeroDocumentoInput.value,
        'Número de RUC': isFactura ? this.numeroRucInput.value : '',
      };

      return fetch(this.cartUpdateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({attributes}),
      }).catch((error) => console.error(error));
    }

    normalizeForAddress(value) {
      return (value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase();
    }

    getCheckoutParams() {
      const isFactura = this.getComprobanteTipo() === 'factura';

      return {
        'checkout[shipping_address][country]': 'PE',
        'checkout[shipping_address][province]': this.departamentoSelect.value,
        'checkout[shipping_address][city]': this.normalizeForAddress(this.distritoSelect.value),
        'checkout[shipping_address][zip]': this.normalizeForAddress(this.provinciaSelect.value),
        comprobante_tipo: isFactura ? 'factura' : 'boleta',
        comprobante_documento_tipo: isFactura ? 'ruc' : this.tipoDocumentoSelect.value,
        comprobante_numero: this.numeroDocumentoInput.value,
        envolver_regalo: 0,
        'attributes[tipo_entrega]': 'Envio a domicilio',
        'checkout[shipping_address][company]': this.numeroDocumentoInput.value,
        company: this.numeroDocumentoInput.value,
      };
    }

    buildCheckoutUrl() {
      const params = this.getCheckoutParams();

      const query = Object.keys(params)
        .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&');

      return `${this.checkoutUrl}?${query}`;
    }

    submitCheckoutForm() {
      const form = document.createElement('form');

      form.method = 'post';
      form.action = this.buildCheckoutUrl();
      form.hidden = true;

      document.body.appendChild(form);
      form.submit();
    }

    validate() {
      if (!this.departamentoSelect.value || !this.provinciaSelect.value || !this.distritoSelect.value) {
        return this.requiredError;
      }

      const isFactura = this.getComprobanteTipo() === 'factura';

      if (isFactura) {
        if (!/^\d{11}$/.test(this.numeroRucInput.value)) return this.invalidRucError;
        return null;
      }

      if (!this.numeroDocumentoInput.value) return this.requiredError;

      if (this.tipoDocumentoSelect.value === 'dni' && !/^\d{8}$/.test(this.numeroDocumentoInput.value)) {
        return this.invalidDniError;
      }

      return null;
    }

    onContinueClick(event) {
      event.preventDefault();
      this.hideError();

      const error = this.validate();

      if (error) {
        this.showError(error);
        return;
      }

      this.continueButton.setAttribute('disabled', 'disabled');

      this.updateCartAttributes()
        .then(() => {
          this.continueButton.removeAttribute('disabled');
          this.submitCheckoutForm();
        })
        .catch((error) => {
          console.error(error);
          this.continueButton.removeAttribute('disabled');
        });
    }

    showError(message) {
      this.errorMessage.textContent = message;
      this.errorMessage.hidden = false;
    }

    hideError() {
      this.errorMessage.hidden = true;
      this.errorMessage.textContent = '';
    }

    open() {
      this.isAnimating = true;
      this.dialog.removeAttribute('inert');
      this.dialog.setAttribute('aria-hidden', 'false');

      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else if (typeof this.dialog.show === 'function') {
        this.dialog.show();
      } else {
        this.dialog.setAttribute('open', '');
      }

      document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true}));

      window.theme.waitForAnimationEnd(this.dialog).then(() => {
        this.isAnimating = false;
        this.a11y?.trapFocus(this.dialog);
        this.departamentoSelect.focus();
      });
    }

    close() {
      if (this.isAnimating || this.dialog.hasAttribute('inert')) return;

      if (!this.dialog.hasAttribute('closing')) {
        this.dialog.setAttribute('closing', '');
        this.isAnimating = true;

        window.theme.waitForAnimationEnd(this.dialog).then(() => {
          this.isAnimating = false;
          this.close();
        });

        return;
      }

      if (typeof this.dialog.close === 'function') {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
      }

      this.dialog.setAttribute('inert', '');
      this.dialog.setAttribute('aria-hidden', 'true');
      this.dialog.removeAttribute('closing');

      if (!window.theme?.hasOpenModals?.()) {
        document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
      }

      this.a11y?.removeTrapFocus();
      this.a11y?.autoFocusLastElement();
    }
  }

  if (!customElements.get('cart-validation-element')) {
    customElements.define('cart-validation-element', CartValidationElement);
  }
})();
